import React, { lazy, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AgencyGuard, AuthGuard, CreatorGuard } from './guards';
import PrivateLayout from './layout/PrivateLayout/PrivateLayout';
import { loadUser } from './store/auth';
import store from './store/store';
import DynamicScrollToTop from './upload/components/layout/DynamicScrollToTop';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';

// Authentication
const Register = lazy(() => import('./upload/components/views/Auth/Register'));
const Login = lazy(() => import('./upload/components/views/Auth/Login'));
const ForgotPassword = lazy(() => import('./upload/components/views/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./upload/components/views/Auth/ResetPassword'));
const EnterPin = lazy(() => import('./upload/components/views/Auth/EnterPin/EnterPin'));
const NotFound = lazy(() => import('./upload/components/views/NotFound'));

// Dashboard
const Dashboard = lazy(() => import('./dashboard/pages/Dashboard/Dashboard'));
const Analytics = lazy(() => import('./dashboard/pages/Analytics/Analytics'));
const MyVideos = lazy(() => import('./dashboard/pages/myVideos/MyVideos'));
const SingleVideo = lazy(
  () => import('./dashboard/pages/myVideos/components/singleVideo/SingleVideo'),
);
const Earnings = lazy(() => import('./dashboard/pages/Earnings/Earnings'));

// Creators
const Creators = lazy(() => import('./dashboard/pages/Creators/Creators'));

// Upload
const Upload = lazy(() => import('./upload/components/views/Upload/Upload'));
const Uploads = lazy(() => import('./upload/components/views/Uploads/Uploads'));

export const App: React.FC = () => {
  useEffect(() => {
    store.dispatch(loadUser());
  }, []);

  return (
    <Provider store={store}>
      <DynamicScrollToTop />
      <Switch>
        <Route component={Register} exact path="/" />
        <Route component={Register} exact path="/register" />
        <Route component={Login} exact path="/login" />
        <Route component={ForgotPassword} exact path="/forgot-password" />
        <Route component={EnterPin} exact path="/enter-pin" />
        <Route component={ResetPassword} exact path="/reset-password" />
        <Route
          exact
          path="/dashboard"
          render={() => (
            <AuthGuard>
              <CreatorGuard>
                <PrivateLayout>
                  <Dashboard />
                </PrivateLayout>
              </CreatorGuard>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/dashboard/analytics"
          render={() => (
            <AuthGuard>
              <CreatorGuard>
                <PrivateLayout>
                  <Analytics />
                </PrivateLayout>
              </CreatorGuard>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/dashboard/myVideos"
          render={() => (
            <AuthGuard>
              <CreatorGuard>
                <PrivateLayout>
                  <MyVideos />
                </PrivateLayout>
              </CreatorGuard>
            </AuthGuard>
          )}
        />
        <Route
          path="/dashboard/myVideos/:id"
          render={() => (
            <AuthGuard>
              <CreatorGuard>
                <PrivateLayout>
                  <SingleVideo />
                </PrivateLayout>
              </CreatorGuard>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/dashboard/earnings"
          render={() => (
            <AuthGuard>
              <CreatorGuard>
                <PrivateLayout>
                  <Earnings />
                </PrivateLayout>
              </CreatorGuard>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/creators"
          render={() => (
            <AuthGuard>
              <AgencyGuard>
                <Creators />
              </AgencyGuard>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/upload"
          render={() => (
            <AuthGuard>
              <PrivateLayout>
                <Upload />
              </PrivateLayout>
            </AuthGuard>
          )}
        />
        <Route
          exact
          path="/uploads"
          render={() => (
            <AuthGuard>
              <PrivateLayout>
                <Uploads />
              </PrivateLayout>
            </AuthGuard>
          )}
        />
        <Route component={NotFound} path="/404" />
        <Redirect from="*" to="/404" />
      </Switch>
    </Provider>
  );
};
